import React, { useState, useRef } from "react";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import {
  PlusIcon,
  TrashIcon,
  ExclamationCircleIcon,
  PencilIcon,
  EyeIcon,
  DocumentTextIcon,
  PlayIcon,
  TagIcon,
  LanguageIcon,
  AcademicCapIcon,
  ClockIcon,
  FolderIcon,
  MagnifyingGlassIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  PhotoIcon,
  CloudArrowUpIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolidIcon } from "@heroicons/react/24/solid";
import { useBlogs } from "../../contexts/BlogContext";
import { Blog, BlogCategory } from "../../firebase/blogService";
import DynamicMarkdownEditor from "../../components/DynamicMarkdownEditor";

const BlogSchema = Yup.object().shape({
  title: Yup.string()
    .required("Title is required")
    .max(100, "Title is too long"),
  excerpt: Yup.string()
    .required("Excerpt is required")
    .max(300, "Excerpt is too long"),
  content: Yup.string().required("Content is required"),
  featuredImage: Yup.string(),
  category: Yup.string().required("Category is required"),
  tags: Yup.array()
    .of(Yup.string().required("Tag is required"))
    .min(1, "At least one tag is required"),
  language: Yup.string()
    .oneOf(["Bengali", "English"], "Invalid language")
    .required("Language is required"),
  difficulty: Yup.string()
    .oneOf(["Beginner", "Intermediate", "Advanced"], "Invalid difficulty")
    .required("Difficulty is required"),
  readingTime: Yup.number()
    .min(1, "Reading time must be at least 1 minute")
    .required("Reading time is required"),
  author: Yup.string().required("Author is required"),
  publishedDate: Yup.string().required("Published date is required"),
  prerequisites: Yup.array().of(Yup.string()),
  learningOutcomes: Yup.array().of(Yup.string()),
  seoKeywords: Yup.array().of(Yup.string()),
  targetAudience: Yup.array().of(Yup.string()),
});

const initialBlogValues: Omit<Blog, "id" | "createdAt" | "updatedAt"> = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  featuredImage: "",
  gallery: [],
  category: "",
  subcategory: "",
  tags: [""],
  language: "English",
  difficulty: "Beginner",
  readingTime: 5,
  youtubeVideoId: "",
  youtubeVideoTitle: "",
  youtubeVideoDescription: "",
  youtubeVideoThumbnail: "",
  codeRepository: "",
  demoUrl: "",
  downloadableResources: [],
  prerequisites: [""],
  learningOutcomes: [""],
  relatedTopics: [],
  seoKeywords: [""],
  metaDescription: "",
  published: false,
  featured: false,
  views: 0,
  likes: 0,
  comments: 0,
  publishedDate: new Date().toISOString().split("T")[0],
  lastUpdated: "",
  author: "",
  authorBio: "",
  authorImage: "",
  estimatedDevelopmentTime: "",
  targetAudience: [""],
  order: 0,
};

const BlogCategorySchema = Yup.object().shape({
  name: Yup.string()
    .required("Category name is required")
    .max(50, "Category name is too long"),
  description: Yup.string().max(200, "Description is too long"),
  subcategories: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required("Subcategory name is required"),
      description: Yup.string(),
    })
  ),
});

const initialCategoryValues: Omit<BlogCategory, "id"> = {
  name: "",
  description: "",
  icon: "",
  color: "#3B82F6",
  subcategories: [],
};

const languageOptions = ["Bengali", "English"];
const difficultyOptions = ["Beginner", "Intermediate", "Advanced"];

const BlogsEditor: React.FC = () => {
  // IMMEDIATE DEBUG LOG - This should appear if component loads at all
  console.log("🟢 BlogsEditor component is loading!");

  // Test if window and console exist
  console.log("🔍 Window object exists:", typeof window !== "undefined");
  console.log("🔍 Console object exists:", typeof console !== "undefined");

  // Add error listener to catch any JavaScript errors
  React.useEffect(() => {
    console.log("🔧 useEffect is running - component mounted");

    const handleError = (event: ErrorEvent) => {
      console.error("🔥 WINDOW ERROR DETECTED:", event.error);
      console.error("🔥 Error message:", event.message);
      console.error("🔥 Error filename:", event.filename);
      console.error("🔥 Error line:", event.lineno);
    };

    window.addEventListener("error", handleError);

    return () => {
      console.log("🔧 useEffect cleanup - component unmounting");
      window.removeEventListener("error", handleError);
    };
  }, []);

  const {
    blogs,
    blogCategories,
    loading,
    error,
    addBlog,
    editBlog,
    removeBlog,
    addBlogCategory,
    editBlogCategory,
    removeBlogCategory,
    moveUp,
    moveDown,
    generateBlogSlug,
  } = useBlogs();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState<Blog | null>(null);
  const [editingCategory, setEditingCategory] = useState<BlogCategory | null>(
    null
  );

  // Debug: Log state changes
  console.log("🔧 BlogsEditor render - isModalOpen:", isModalOpen);
  console.log("🔧 BlogsEditor render - editingBlog:", editingBlog);
  console.log("🔧 BlogsEditor render - blogs:", blogs?.length || 0, "blogs");
  console.log("🔧 BlogsEditor render - loading:", loading);
  console.log("🔧 BlogsEditor render - error:", error);

  const [deleteConfirmationId, setDeleteConfirmationId] = useState<
    string | null
  >(null);
  const [deleteCategoryConfirmationId, setDeleteCategoryConfirmationId] =
    useState<string | null>(null);

  // Featured Image File Upload
  const [uploading, setUploading] = useState(false);
  const [selectedFeaturedImageFile, setSelectedFeaturedImageFile] =
    useState<File | null>(null);
  const featuredImageInputRef = useRef<HTMLInputElement>(null);

  // Filters
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [filterLanguage, setFilterLanguage] = useState<string>("all");
  const [filterDifficulty, setFilterDifficulty] = useState<string>("all");
  const [filterPublished, setFilterPublished] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState<string>("");

  const filteredBlogs = blogs
    .filter((blog) => {
      const categoryMatch =
        filterCategory === "all" || blog.category === filterCategory;
      const languageMatch =
        filterLanguage === "all" || blog.language === filterLanguage;
      const difficultyMatch =
        filterDifficulty === "all" || blog.difficulty === filterDifficulty;
      const publishedMatch =
        filterPublished === "all" ||
        (filterPublished === "published" && blog.published) ||
        (filterPublished === "draft" && !blog.published);
      const searchMatch =
        searchTerm === "" ||
        blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.tags.some((tag) =>
          tag.toLowerCase().includes(searchTerm.toLowerCase())
        );

      return (
        categoryMatch &&
        languageMatch &&
        difficultyMatch &&
        publishedMatch &&
        searchMatch
      );
    })
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const handleBlogSubmit = async (
    values: typeof initialBlogValues,
    { setSubmitting, resetForm }: any
  ) => {
    console.log("🎯 handleBlogSubmit called!");
    console.log("🎯 Values received:", values);
    console.log("🎯 Editing blog:", editingBlog);

    try {
      // Generate slug from title if not provided
      if (!values.slug) {
        values.slug = generateBlogSlug(values.title);
      }

      // Set order for new blogs
      if (!editingBlog && !values.order) {
        const maxOrder = blogs.reduce(
          (max, blog) => Math.max(max, blog.order || 0),
          0
        );
        values.order = maxOrder + 1;
      }

      if (editingBlog) {
        console.log("🎯 Editing existing blog...");
        await editBlog(editingBlog.id!, values);
        toast.success("Blog updated successfully!");
      } else {
        console.log("🎯 Creating new blog...");
        await addBlog(values);
        toast.success("Blog created successfully!");
      }
      setIsModalOpen(false);
      setEditingBlog(null);
      setSelectedFeaturedImageFile(null);
      resetForm();
    } catch (err) {
      console.error("❌ Error saving blog:", err);
      toast.error("Failed to save blog. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCategorySubmit = async (
    values: typeof initialCategoryValues,
    { setSubmitting, resetForm }: any
  ) => {
    try {
      if (editingCategory) {
        await editBlogCategory(editingCategory.id, values);
        toast.success("Category updated successfully!");
      } else {
        await addBlogCategory(values);
        toast.success("Category created successfully!");
      }
      setIsCategoryModalOpen(false);
      setEditingCategory(null);
      resetForm();
    } catch (err) {
      console.error("Error saving category:", err);
      toast.error("Failed to save category. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditBlog = (blog: Blog) => {
    setEditingBlog(blog);
    setSelectedFeaturedImageFile(null);
    setIsModalOpen(true);
  };

  const handleEditCategory = (category: BlogCategory) => {
    setEditingCategory(category);
    setIsCategoryModalOpen(true);
  };

  // Featured Image File Handling
  const handleFeaturedImageFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Supported image types
    const supportedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
      "image/svg+xml",
    ];

    if (supportedTypes.includes(file.type) || file.type.startsWith("image/")) {
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }
      setSelectedFeaturedImageFile(file);
    } else {
      toast.error(
        "Please select a valid image file (JPG, PNG, GIF, WebP, SVG)"
      );
    }
    event.target.value = "";
  };

  const uploadFeaturedImageFile = async (setFieldValue: any) => {
    if (!selectedFeaturedImageFile) return;

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setFieldValue("featuredImage", result);
        setSelectedFeaturedImageFile(null);
        setUploading(false);
        toast.success("Featured image uploaded successfully!");
      };
      reader.onerror = () => {
        setUploading(false);
        toast.error("Failed to read file. Please try again.");
      };
      reader.readAsDataURL(selectedFeaturedImageFile);
    } catch (error) {
      setUploading(false);
      toast.error("Failed to upload featured image. Please try again.");
    }
  };

  const removeSelectedFeaturedImageFile = () => {
    setSelectedFeaturedImageFile(null);
  };

  const handleDeleteBlog = async (id: string) => {
    try {
      await removeBlog(id);
      toast.success("Blog deleted successfully!");
      setDeleteConfirmationId(null);
    } catch (err) {
      console.error("Error deleting blog:", err);
      toast.error("Failed to delete blog. Please try again.");
    }
  };

  const handleDeleteCategory = async (id: string) => {
    try {
      await removeBlogCategory(id);
      toast.success("Category deleted successfully!");
      setDeleteCategoryConfirmationId(null);
    } catch (err) {
      console.error("Error deleting category:", err);
      toast.error("Failed to delete category. Please try again.");
    }
  };

  const handleMoveUp = async (id: string) => {
    try {
      await moveUp(id);
      toast.success("Blog moved up!");
    } catch (err) {
      console.error("Error moving blog up:", err);
      toast.error("Failed to move blog.");
    }
  };

  const handleMoveDown = async (id: string) => {
    try {
      await moveDown(id);
      toast.success("Blog moved down!");
    } catch (err) {
      console.error("Error moving blog down:", err);
      toast.error("Failed to move blog.");
    }
  };

  const extractYouTubeVideoId = (url: string) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading blogs
            </h3>
            <p className="mt-2 text-sm text-red-700">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Coding Blogs
          </h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            Create and manage your coding tutorial blogs with YouTube
            integration and rich content.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none space-x-3">
          <button
            type="button"
            onClick={() => {
              setEditingCategory(null);
              setIsCategoryModalOpen(true);
            }}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <FolderIcon className="h-4 w-4 mr-2" />
            Manage Categories
          </button>

          <button
            type="button"
            onClick={() => {
              setEditingBlog(null);
              setIsModalOpen(true);
            }}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            New Blog
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Search
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                placeholder="Search blogs..."
              />
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Category
            </label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            >
              <option value="all">All Categories</option>
              {blogCategories.map((category) => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Language
            </label>
            <select
              value={filterLanguage}
              onChange={(e) => setFilterLanguage(e.target.value)}
              className="block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            >
              <option value="all">All Languages</option>
              {languageOptions.map((language) => (
                <option key={language} value={language}>
                  {language}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Difficulty
            </label>
            <select
              value={filterDifficulty}
              onChange={(e) => setFilterDifficulty(e.target.value)}
              className="block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            >
              <option value="all">All Levels</option>
              {difficultyOptions.map((difficulty) => (
                <option key={difficulty} value={difficulty}>
                  {difficulty}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <select
              value={filterPublished}
              onChange={(e) => setFilterPublished(e.target.value)}
              className="block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            >
              <option value="all">All</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>
      </div>

      {/* Blogs List */}
      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {filteredBlogs.map((blog) => (
            <li key={blog.id} className="px-6 py-4">
              <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                {/* Blog Featured Image */}
                <div className="flex-shrink-0">
                  {blog.featuredImage ? (
                    <img
                      className="h-16 w-20 rounded-lg object-cover"
                      src={blog.featuredImage}
                      alt={blog.title}
                    />
                  ) : (
                    <div className="h-16 w-20 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                      <DocumentTextIcon className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Blog Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      {blog.title}
                    </h3>
                    {!blog.published && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                        Draft
                      </span>
                    )}
                    {blog.featured && (
                      <StarSolidIcon className="h-5 w-5 text-yellow-400" />
                    )}
                    {blog.youtubeVideoId && (
                      <PlayIcon className="h-5 w-5 text-red-600" />
                    )}
                  </div>
                  <p
                    className="text-sm text-gray-500 dark:text-gray-400 mb-2 overflow-hidden"
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {blog.excerpt}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400">
                    <span className="inline-flex items-center">
                      <TagIcon className="h-3 w-3 mr-1" />
                      {blog.category}
                    </span>
                    <span className="inline-flex items-center">
                      <LanguageIcon className="h-3 w-3 mr-1" />
                      {blog.language}
                    </span>
                    <span className="inline-flex items-center">
                      <AcademicCapIcon className="h-3 w-3 mr-1" />
                      {blog.difficulty}
                    </span>
                    <span className="inline-flex items-center">
                      <ClockIcon className="h-3 w-3 mr-1" />
                      {blog.readingTime} min read
                    </span>
                    <span>
                      👁 {blog.views || 0} • ❤ {blog.likes || 0}
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-shrink-0 items-center space-x-2 sm:mt-0 mt-2">
                  {blog.published && blog.slug && (
                    <a
                      href={`/blogs/${blog.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-8 h-8 text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                      title="View Blog"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </a>
                  )}

                  <button
                    onClick={() => handleEditBlog(blog)}
                    className="inline-flex items-center justify-center w-8 h-8 text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded"
                    title="Edit"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>

                  <button
                    onClick={() => handleMoveUp(blog.id!)}
                    className="inline-flex items-center justify-center w-8 h-8 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Move Up"
                    disabled={blogs.findIndex((b) => b.id === blog.id) === 0}
                  >
                    <ArrowUpIcon className="h-4 w-4" />
                  </button>

                  <button
                    onClick={() => handleMoveDown(blog.id!)}
                    className="inline-flex items-center justify-center w-8 h-8 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Move Down"
                    disabled={
                      blogs.findIndex((b) => b.id === blog.id) ===
                      blogs.length - 1
                    }
                  >
                    <ArrowDownIcon className="h-4 w-4" />
                  </button>

                  <button
                    onClick={() => setDeleteConfirmationId(blog.id!)}
                    className="inline-flex items-center justify-center w-8 h-8 text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    title="Delete"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>

        {filteredBlogs.length === 0 && (
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              No blogs found
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchTerm ||
              filterCategory !== "all" ||
              filterLanguage !== "all" ||
              filterDifficulty !== "all" ||
              filterPublished !== "all"
                ? "No blogs match the current filters."
                : "Get started by creating your first blog post."}
            </p>
          </div>
        )}
      </div>

      {/* Blog Modal for Add/Edit */}
      {(() => {
        console.log(
          "🔍 Modal render check - isModalOpen:",
          isModalOpen,
          "editingBlog:",
          editingBlog
        );
        return null;
      })()}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {(() => {
            console.log("🎯 Modal is rendering!");
            return null;
          })()}
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => {
                setIsModalOpen(false);
                setSelectedFeaturedImageFile(null);
              }}
            />

            <div
              className="relative bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full sm:p-6 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                  {editingBlog ? "Edit Blog Post" : "Create New Blog Post"}
                </h3>
              </div>

              <Formik
                initialValues={
                  editingBlog
                    ? {
                        ...editingBlog,
                        gallery: editingBlog.gallery || [],
                        tags:
                          editingBlog.tags.length > 0 ? editingBlog.tags : [""],
                        prerequisites:
                          editingBlog.prerequisites.length > 0
                            ? editingBlog.prerequisites
                            : [""],
                        learningOutcomes:
                          editingBlog.learningOutcomes.length > 0
                            ? editingBlog.learningOutcomes
                            : [""],
                        seoKeywords:
                          editingBlog.seoKeywords.length > 0
                            ? editingBlog.seoKeywords
                            : [""],
                        targetAudience:
                          editingBlog.targetAudience.length > 0
                            ? editingBlog.targetAudience
                            : [""],
                        relatedTopics: editingBlog.relatedTopics || [],
                        downloadableResources:
                          editingBlog.downloadableResources || [],
                      }
                    : initialBlogValues
                }
                validationSchema={BlogSchema}
                onSubmit={handleBlogSubmit}
                enableReinitialize
                validateOnChange={false}
                validateOnBlur={false}
              >
                {({ values, setFieldValue, isSubmitting, errors, touched }) => {
                  console.log("🎯 FORMIK RENDER - Values:", values);
                  console.log("🎯 FORMIK RENDER - Errors:", errors);
                  console.log("🎯 FORMIK RENDER - Touched:", touched);
                  console.log("🎯 FORMIK RENDER - isSubmitting:", isSubmitting);

                  return (
                    <Form className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Basic Information */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Title *
                          </label>
                          <Field
                            name="title"
                            type="text"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="e.g., Complete Guide to Flutter State Management"
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              setFieldValue("title", e.target.value);
                              // Auto-generate slug from title
                              setFieldValue(
                                "slug",
                                generateBlogSlug(e.target.value)
                              );
                            }}
                          />
                          <ErrorMessage
                            name="title"
                            component="div"
                            className="mt-1 text-sm text-red-600"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            URL Slug
                          </label>
                          <Field
                            name="slug"
                            type="text"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="complete-guide-flutter-state-management"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            URL-friendly version of the title
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Reading Time (minutes) *
                          </label>
                          <Field
                            name="readingTime"
                            type="number"
                            min="1"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="5"
                          />
                          <ErrorMessage
                            name="readingTime"
                            component="div"
                            className="mt-1 text-sm text-red-600"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Excerpt *
                          </label>
                          <Field
                            as="textarea"
                            name="excerpt"
                            rows={3}
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="A brief summary that will appear in blog listings and search results"
                          />
                          <ErrorMessage
                            name="excerpt"
                            component="div"
                            className="mt-1 text-sm text-red-600"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Content *
                          </label>
                          <Field name="content">
                            {({ field, form, meta }: any) => (
                              <DynamicMarkdownEditor
                                value={field.value}
                                onChange={(value) =>
                                  form.setFieldValue("content", value)
                                }
                                placeholder="Write your blog content here. You can embed YouTube videos, images, code blocks, and more!"
                                error={
                                  meta.touched && meta.error
                                    ? meta.error
                                    : undefined
                                }
                              />
                            )}
                          </Field>
                        </div>

                        {/* Featured Image */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Featured Image
                          </label>

                          {/* Hidden file input */}
                          <input
                            ref={featuredImageInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFeaturedImageFileSelect}
                            className="hidden"
                          />

                          <div className="space-y-3">
                            {/* Current Featured Image */}
                            {values.featuredImage &&
                              !selectedFeaturedImageFile && (
                                <div className="relative">
                                  <img
                                    src={values.featuredImage}
                                    alt="Current featured"
                                    className="h-32 w-48 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setFieldValue("featuredImage", "")
                                    }
                                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              )}

                            {/* Selected File Preview */}
                            {selectedFeaturedImageFile && (
                              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3">
                                    <PhotoIcon className="h-8 w-8 text-blue-600" />
                                    <div>
                                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                                        {selectedFeaturedImageFile.name}
                                      </p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        {(
                                          selectedFeaturedImageFile.size /
                                          1024 /
                                          1024
                                        ).toFixed(2)}{" "}
                                        MB
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex space-x-2">
                                    <button
                                      type="button"
                                      onClick={removeSelectedFeaturedImageFile}
                                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        uploadFeaturedImageFile(setFieldValue)
                                      }
                                      disabled={uploading}
                                      className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                    >
                                      {uploading ? (
                                        <>
                                          <svg
                                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                          >
                                            <circle
                                              className="opacity-25"
                                              cx="12"
                                              cy="12"
                                              r="10"
                                              stroke="currentColor"
                                              strokeWidth="4"
                                            ></circle>
                                            <path
                                              className="opacity-75"
                                              fill="currentColor"
                                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                          </svg>
                                          Uploading...
                                        </>
                                      ) : (
                                        <>
                                          <CloudArrowUpIcon className="h-4 w-4 mr-1" />
                                          Upload
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Upload Button */}
                            {!selectedFeaturedImageFile && (
                              <button
                                type="button"
                                onClick={() =>
                                  featuredImageInputRef.current?.click()
                                }
                                className="w-full flex justify-center items-center px-6 py-4 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                <PhotoIcon className="mx-auto h-8 w-8" />
                                <span className="ml-2">
                                  {values.featuredImage
                                    ? "Change Featured Image"
                                    : "Upload Featured Image"}
                                </span>
                              </button>
                            )}
                          </div>

                          <p className="mt-2 text-xs text-gray-500">
                            Main image that will be displayed at the top of the
                            blog post and in listings. Supported formats: JPG,
                            PNG, GIF, WebP, SVG. Max size: 5MB.
                          </p>
                        </div>

                        {/* Categories and Tags */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Category *
                          </label>
                          <Field
                            as="select"
                            name="category"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                          >
                            <option value="">Select a category</option>
                            {blogCategories.map((category) => (
                              <option key={category.id} value={category.name}>
                                {category.name}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage
                            name="category"
                            component="div"
                            className="mt-1 text-sm text-red-600"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Subcategory
                          </label>
                          <Field
                            name="subcategory"
                            type="text"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="e.g., State Management, UI Components"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Language *
                          </label>
                          <Field
                            as="select"
                            name="language"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                          >
                            {languageOptions.map((language) => (
                              <option key={language} value={language}>
                                {language}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage
                            name="language"
                            component="div"
                            className="mt-1 text-sm text-red-600"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Difficulty *
                          </label>
                          <Field
                            as="select"
                            name="difficulty"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                          >
                            {difficultyOptions.map((difficulty) => (
                              <option key={difficulty} value={difficulty}>
                                {difficulty}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage
                            name="difficulty"
                            component="div"
                            className="mt-1 text-sm text-red-600"
                          />
                        </div>

                        {/* YouTube Integration */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            YouTube Video URL
                          </label>
                          <Field
                            name="youtubeVideoId"
                            type="text"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="https://www.youtube.com/watch?v=VIDEO_ID or just VIDEO_ID"
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              const url = e.target.value;
                              const videoId = extractYouTubeVideoId(url) || url;
                              setFieldValue("youtubeVideoId", videoId);

                              // Auto-generate thumbnail URL
                              if (videoId && videoId.length === 11) {
                                setFieldValue(
                                  "youtubeVideoThumbnail",
                                  `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
                                );
                              }
                            }}
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Paste YouTube URL or video ID for tutorial
                            integration
                          </p>
                        </div>

                        {values.youtubeVideoId && (
                          <>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                YouTube Video Title
                              </label>
                              <Field
                                name="youtubeVideoTitle"
                                type="text"
                                className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                                placeholder="Title of the YouTube video"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                YouTube Video Description
                              </label>
                              <Field
                                as="textarea"
                                name="youtubeVideoDescription"
                                rows={2}
                                className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                                placeholder="Brief description of the video content"
                              />
                            </div>
                          </>
                        )}

                        {/* URLs */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Code Repository
                          </label>
                          <Field
                            name="codeRepository"
                            type="url"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="https://github.com/username/repository"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Demo URL
                          </label>
                          <Field
                            name="demoUrl"
                            type="url"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="https://demo.example.com"
                          />
                        </div>

                        {/* Author Information */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Author *
                          </label>
                          <Field
                            name="author"
                            type="text"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="Your name"
                          />
                          <ErrorMessage
                            name="author"
                            component="div"
                            className="mt-1 text-sm text-red-600"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Published Date *
                          </label>
                          <Field
                            name="publishedDate"
                            type="date"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                          />
                          <ErrorMessage
                            name="publishedDate"
                            component="div"
                            className="mt-1 text-sm text-red-600"
                          />
                        </div>

                        {/* Dynamic Arrays */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Tags *
                          </label>
                          <FieldArray name="tags">
                            {({ remove, push }) => (
                              <div className="space-y-2">
                                {Array.isArray(values.tags) &&
                                  values.tags.map((_, index) => (
                                    <div
                                      key={index}
                                      className="flex items-center space-x-2"
                                    >
                                      <Field
                                        name={`tags.${index}`}
                                        type="text"
                                        className="flex-1 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                                        placeholder="e.g., flutter, react, javascript"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => remove(index)}
                                        className="text-red-600 hover:text-red-800"
                                      >
                                        <TrashIcon className="h-4 w-4" />
                                      </button>
                                    </div>
                                  ))}
                                <button
                                  type="button"
                                  onClick={() => push("")}
                                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                                >
                                  <PlusIcon className="h-4 w-4 mr-1" />
                                  Add Tag
                                </button>
                              </div>
                            )}
                          </FieldArray>
                          <ErrorMessage
                            name="tags"
                            component="div"
                            className="mt-1 text-sm text-red-600"
                          />
                        </div>

                        {/* Published and Featured */}
                        <div className="md:col-span-2 space-y-4">
                          <div className="flex items-center space-x-6">
                            <div className="flex items-center">
                              <Field
                                name="published"
                                type="checkbox"
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <label className="ml-2 block text-sm text-gray-900 dark:text-white">
                                Published
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  Make this blog visible to the public
                                </p>
                              </label>
                            </div>

                            <div className="flex items-center">
                              <Field
                                name="featured"
                                type="checkbox"
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <label className="ml-2 block text-sm text-gray-900 dark:text-white">
                                Featured Blog
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  Feature this blog on the homepage
                                </p>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Modal Actions */}
                      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <button
                          type="button"
                          onClick={() => {
                            setIsModalOpen(false);
                            setSelectedFeaturedImageFile(null);
                          }}
                          className="bg-white dark:bg-gray-700 py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          onClick={() => {
                            console.log(
                              "🎯 CREATE BLOG SUBMIT BUTTON CLICKED!"
                            );
                            console.log("🎯 isSubmitting:", isSubmitting);
                          }}
                          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                          {isSubmitting
                            ? "Saving..."
                            : editingBlog
                            ? "Update Blog"
                            : "Create Blog"}
                        </button>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {isCategoryModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setIsCategoryModalOpen(false)}
            />

            <div
              className="relative bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                  Manage Blog Categories
                </h3>
              </div>

              {/* Existing Categories */}
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                  Existing Categories
                </h4>
                <div className="space-y-2">
                  {blogCategories.map((category) => (
                    <div
                      key={category.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                    >
                      <div>
                        <h5 className="font-medium text-gray-900 dark:text-white">
                          {category.name}
                        </h5>
                        {category.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {category.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditCategory(category)}
                          className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() =>
                            setDeleteCategoryConfirmationId(category.id)
                          }
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Add/Edit Category Form */}
              <Formik
                initialValues={
                  editingCategory ? editingCategory : initialCategoryValues
                }
                validationSchema={BlogCategorySchema}
                onSubmit={handleCategorySubmit}
                enableReinitialize
              >
                {({ values, isSubmitting }) => (
                  <Form className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Category Name *
                      </label>
                      <Field
                        name="name"
                        type="text"
                        className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                        placeholder="e.g., Flutter, React, Node.js"
                      />
                      <ErrorMessage
                        name="name"
                        component="div"
                        className="mt-1 text-sm text-red-600"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Description
                      </label>
                      <Field
                        as="textarea"
                        name="description"
                        rows={2}
                        className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                        placeholder="Brief description of this category"
                      />
                      <ErrorMessage
                        name="description"
                        component="div"
                        className="mt-1 text-sm text-red-600"
                      />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setIsCategoryModalOpen(false);
                          setEditingCategory(null);
                        }}
                        className="bg-white dark:bg-gray-700 py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                      >
                        {isSubmitting
                          ? "Saving..."
                          : editingCategory
                          ? "Update Category"
                          : "Add Category"}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      )}

      {/* Delete Blog Confirmation Modal */}
      {deleteConfirmationId && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setDeleteConfirmationId(null)}
            />

            <div
              className="relative bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationCircleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                    Delete Blog Post
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Are you sure you want to delete this blog post? This
                      action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => handleDeleteBlog(deleteConfirmationId)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteConfirmationId(null)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-700 text-base font-medium text-gray-700 dark:text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Category Confirmation Modal */}
      {deleteCategoryConfirmationId && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setDeleteCategoryConfirmationId(null)}
            />

            <div
              className="relative bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationCircleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                    Delete Category
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Are you sure you want to delete this category? This action
                      cannot be undone and may affect existing blog posts.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() =>
                    handleDeleteCategory(deleteCategoryConfirmationId)
                  }
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteCategoryConfirmationId(null)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-700 text-base font-medium text-gray-700 dark:text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogsEditor;
